import React, { useState } from 'react';
import { trackBlogArticleClick } from '../components/GoogleAnalytics';
import {
  Container,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  CardActionArea,
  Chip,
  Tabs,
  Tab,
  Avatar,
  Skeleton,
  Alert,
} from '@mui/material';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import SEO from '../components/SEO';
import { useBlogIndex } from '../hooks/useBlogData';
import type { BlogPostSummary } from '../types/blog';

/**
 * Format date to readable string
 */
function formatDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

/**
 * Article card component for displaying post summaries
 */
const ArticleCard: React.FC<{ post: BlogPostSummary }> = ({ post }) => {
  return (
    <Card
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        transition: 'transform 0.2s, box-shadow 0.2s',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: 4,
        },
      }}
    >
      <CardActionArea
        component={RouterLink}
        to={`/blog/${post.slug}`}
        onClick={() => trackBlogArticleClick(post.title)}
        sx={{
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'stretch',
        }}
      >
        {/* Featured image placeholder - shown only if featuredImage exists */}
        {post.featuredImage && (
          <Box
            sx={{
              height: 200,
              backgroundImage: `url(${post.featuredImage})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
            }}
          />
        )}
        <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
          {/* Category chip */}
          <Chip label={post.category} size="small" color="primary" sx={{ alignSelf: 'flex-start', mb: 1.5 }} />

          {/* Title */}
          <Typography
            variant="h6"
            component="h2"
            gutterBottom
            sx={{
              fontWeight: 600,
              lineHeight: 1.3,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
            }}
          >
            {post.title}
          </Typography>

          {/* Excerpt */}
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              mb: 2,
              flexGrow: 1,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              display: '-webkit-box',
              WebkitLineClamp: 3,
              WebkitBoxOrient: 'vertical',
            }}
          >
            {post.excerpt}
          </Typography>

          {/* Author and meta info */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              mt: 'auto',
            }}
          >
            {/* Author */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Avatar src={post.author.avatar || undefined} alt={post.author.name} sx={{ width: 32, height: 32 }}>
                {post.author.name.charAt(0)}
              </Avatar>
              <Typography variant="caption" color="text.secondary">
                {post.author.name}
              </Typography>
            </Box>

            {/* Date and reading time */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <AccessTimeIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
              <Typography variant="caption" color="text.secondary">
                {post.readingTime} min read
              </Typography>
            </Box>
          </Box>

          {/* Published date */}
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
            {formatDate(post.publishedDate)}
          </Typography>
        </CardContent>
      </CardActionArea>
    </Card>
  );
};

/**
 * Loading skeleton for article cards
 */
const ArticleCardSkeleton: React.FC = () => (
  <Card sx={{ height: '100%' }}>
    <CardContent>
      <Skeleton variant="rectangular" width={80} height={24} sx={{ mb: 1.5, borderRadius: 1 }} />
      <Skeleton variant="text" sx={{ fontSize: '1.5rem' }} />
      <Skeleton variant="text" sx={{ fontSize: '1.5rem', width: '60%' }} />
      <Skeleton variant="text" sx={{ mt: 1 }} />
      <Skeleton variant="text" />
      <Skeleton variant="text" sx={{ width: '80%' }} />
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2 }}>
        <Skeleton variant="circular" width={32} height={32} />
        <Skeleton variant="text" width={100} />
      </Box>
    </CardContent>
  </Card>
);

const Blog: React.FC = () => {
  const { data: blogIndex, isLoading, error } = useBlogIndex();
  const [searchParams, setSearchParams] = useSearchParams();
  const categoryFromUrl = searchParams.get('category');
  const [selectedCategory, setSelectedCategory] = useState<string | null>(categoryFromUrl);

  // Sync state with URL when URL changes
  React.useEffect(() => {
    setSelectedCategory(categoryFromUrl);
  }, [categoryFromUrl]);

  // Filter posts by selected category
  const filteredPosts =
    selectedCategory && blogIndex
      ? blogIndex.posts.filter((post) => post.category === selectedCategory)
      : blogIndex?.posts || [];

  const handleCategoryChange = (_event: React.SyntheticEvent, newValue: string | null) => {
    setSelectedCategory(newValue);
    if (newValue) {
      setSearchParams({ category: newValue });
    } else {
      setSearchParams({});
    }
  };

  return (
    <>
      <SEO
        title="Articles | RobertGarza.us - Family Court Reform Resources"
        description="Expert insights on family court reform, parental rights, and judicial accountability. Learn about parental alienation laws, equal custody, and how to advocate for change."
        path="/blog"
      />

      <Container maxWidth="lg" sx={{ py: 4 }}>
        {/* Page header */}
        <Box sx={{ mb: 4, textAlign: 'center' }}>
          <Typography variant="h3" component="h1" gutterBottom sx={{ fontWeight: 700 }}>
            Articles
          </Typography>
          <Typography variant="h6" color="text.secondary" sx={{ maxWidth: 600, mx: 'auto' }}>
            Expert insights and guides on family court reform, parental rights, and judicial accountability.
          </Typography>
        </Box>

        {/* Category filter tabs - only show if categories exist */}
        {blogIndex && blogIndex.activeCategories.length > 0 && (
          <Box sx={{ mb: 4, borderBottom: 1, borderColor: 'divider' }}>
            <Tabs
              value={selectedCategory}
              onChange={handleCategoryChange}
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
              sx={{
                '& .MuiTab-root': {
                  textTransform: 'none',
                  fontWeight: 500,
                  fontSize: '1rem',
                },
              }}
            >
              <Tab label="All Articles" value={null} />
              {blogIndex.activeCategories.map((category) => (
                <Tab key={category} label={category} value={category} />
              ))}
            </Tabs>
          </Box>
        )}

        {/* Error state */}
        {error && (
          <Alert severity="error" sx={{ mb: 4 }}>
            Failed to load articles. Please try again later.
          </Alert>
        )}

        {/* Loading state */}
        {isLoading && (
          <Grid container spacing={3}>
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <Grid item xs={12} sm={6} md={4} key={i}>
                <ArticleCardSkeleton />
              </Grid>
            ))}
          </Grid>
        )}

        {/* Empty state */}
        {!isLoading && !error && filteredPosts.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <Typography variant="h5" color="text.secondary" gutterBottom>
              No articles found
            </Typography>
            <Typography color="text.secondary">
              {selectedCategory
                ? `No articles in the "${selectedCategory}" category yet.`
                : 'Check back soon for new content.'}
            </Typography>
          </Box>
        )}

        {/* Articles grid */}
        {!isLoading && !error && filteredPosts.length > 0 && (
          <Grid container spacing={3}>
            {filteredPosts.map((post) => (
              <Grid item xs={12} sm={6} md={4} key={post.slug}>
                <ArticleCard post={post} />
              </Grid>
            ))}
          </Grid>
        )}

        {/* Post count */}
        {!isLoading && blogIndex && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 4, textAlign: 'center' }}>
            Showing {filteredPosts.length} of {blogIndex._metadata.totalPosts} articles
          </Typography>
        )}
      </Container>
    </>
  );
};

export default Blog;
